import { createServerFn } from "@tanstack/react-start";
import { and, desc, eq, sql } from "drizzle-orm";
import { z } from "zod";

import { requireAuth } from "@/auth/middleware";
import { getCurrentUserId, getDb, schema } from "@/db/client";
import { sanitizeForSeroval } from "@/server/seroval-safe";
import type { Ratecard } from "@/calc";
import { hasLegacyDivision, renameEstimateDivisions } from "./divisions";
import { auditEstimate } from "./estimate-audit";
import { fromRow, indexColumns } from "./estimate-row";
import { ratecard as seedRatecard } from "./mocks";
import { freezeEstimateRates } from "./rates";
import { guardReview } from "./review-flow";
import { ensureBot } from "./telegram-bot";
import { TRASH_DAYS, type BrainingEstimate } from "./types";

/**
 * Хранение калькуляторов.
 *
 * Смета уезжает в jsonb целиком. Схему пока не нормализуем: форма экранов
 * ещё меняется, и каждая правка типов стоила бы миграции. Колонки
 * title/client/status/archived_at/trashed_at — производный индекс для
 * списка; они пересчитываются из объекта при каждой записи и никогда не
 * читаются обратно, поэтому разъехаться с `data` не могут.
 *
 * Доступ закрыт RLS (0009_estimates.sql): менеджер видит свои калькуляторы,
 * руководитель и админ — все. Проверка чужой сметы это работа руководителя,
 * поэтому ему открыта и запись.
 */

// Валидируем только оболочку: содержимое сметы — это `BrainingEstimate`,
// описывать его вторым зодовским деревом значит поддерживать две схемы,
// которые обязательно разъедутся. От мусора защищает то, что писать может
// только автор или руководитель.
const EstimatePayload = z.object({
  id: z.string().uuid().optional(),
  data: z.record(z.string(), z.unknown()),
});

const IdOnly = z.object({ id: z.string().uuid() });
const IdFlag = z.object({ id: z.string().uuid(), on: z.boolean() });

/** Действующая рейткарта — для снимка ставок в сметах, собранных до него. */
async function currentRatecard(): Promise<Ratecard> {
  const [row] = await getDb().select({ data: schema.ratecard.data }).from(schema.ratecard);
  return (row?.data as Ratecard) ?? seedRatecard;
}

const _loadEstimates = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .handler(async (): Promise<BrainingEstimate[]> => {
    const db = getDb();

    /*
     * Бот согласования поднимается здесь же.
     *
     * Он слушает ответы руководителя опросом, а значит должен работать, пока
     * работает приложение. Отдельного «старта сервера» в этой сборке нет, и
     * подъём только при отправке уведомления означал бы, что после каждого
     * перезапуска нажатая в Телеграме кнопка ждёт следующей отправки. Список
     * калькуляторов открывают первым делом каждый рабочий день — этого
     * достаточно. Вызов идемпотентен и ничего не ждёт.
     */
    ensureBot();

    /*
     * Корзина чистится сама.
     *
     * На экране написано «через 30 дней удалим», в типах — то же обещание, а
     * чистить было некому: ни серверной функции, ни задания в cron. Смета
     * «лежит 0 дней» и лежала дальше. Дешевле всего сделать это здесь: список
     * открывают каждый рабочий день, а удаление идёт по RLS — свои чистит
     * менеджер, все чистят руководитель и админ.
     */
    await db.delete(schema.estimates).where(
      sql`${schema.estimates.trashedAt} is not null
            and ${schema.estimates.trashedAt} < now() - ${`${TRASH_DAYS} days`}::interval`,
    );

    const rows = await db
      .select({ id: schema.estimates.id, data: schema.estimates.data })
      .from(schema.estimates)
      .orderBy(desc(schema.estimates.updatedAt));

    /*
     * Пустой список остаётся пустым.
     *
     * Раньше первый вход заводил демонстрационный калькулятор: экран без
     * данных нечего было показать. С переходом на живую работу это вредно —
     * у менеджера в списке появлялся чужой проект с вымышленным заказчиком, и
     * отличить его от настоящего можно было только по памяти. Пустой экран
     * честнее: на нём написано, что калькуляторов нет, и предложено собрать
     * первый.
     *
     * Сама демо-смета никуда не делась и осталась фикстурой: на ней держатся
     * тесты расчётного ядра — заморозка ставок, отсрочка, переименование
     * подразделений. В базу она больше не попадает.
     */
    const loaded = rows.map(fromRow);

    /*
     * Разовые починки старых калькуляторов.
     *
     * Заморозка ставок: сметы, собранные до редактируемой рейткарты, хранят
     * только названия ролей — ставка подтягивалась при каждом пересчёте.
     * Первая же правка прайса пересчитала бы их задним числом. Поэтому при
     * первом чтении фиксируем в них сегодняшние ставки: то, по чему их и
     * считали. Признак «уже заморожена» — `ratecardVersion`.
     *
     * Подразделения: Brands и Digital стали Comm, Expo расформировали
     * (`divisions.ts`). Признак — само имя из прежней структуры.
     *
     * Оба прохода одноразовые: после записи признак пропадает, и повторное
     * чтение ничего не трогает.
     */
    const card = await currentRatecard();
    const fixed = new Map<string, BrainingEstimate>();
    for (const e of loaded) {
      let next = e.ratecardVersion ? e : freezeEstimateRates(e, card);
      if (hasLegacyDivision(next)) next = renameEstimateDivisions(next);
      if (next !== e) fixed.set(e.id, next);
    }

    if (fixed.size === 0) return sanitizeForSeroval("loadEstimates", loaded);

    for (const e of fixed.values()) {
      await db.update(schema.estimates).set({ data: e }).where(eq(schema.estimates.id, e.id));
    }

    return sanitizeForSeroval(
      "loadEstimates",
      loaded.map((e) => fixed.get(e.id) ?? e),
    );
  });

/** Запись не состоялась: в базе версия новее той, от которой правили. */
export interface SaveConflict {
  conflict: true;
  current: BrainingEstimate;
}

const _saveEstimate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => EstimatePayload.parse(d))
  .handler(async ({ data, context }): Promise<BrainingEstimate | SaveConflict> => {
    const db = getDb();
    const incoming = data.data as unknown as BrainingEstimate;

    if (data.id) {
      // Прежняя версия нужна аудиту: «правили после проверки» видно только в
      // сравнении. Одна строка по ключу — дешевле, чем пропустить нарушение.
      const [before] = await db
        .select({ id: schema.estimates.id, data: schema.estimates.data })
        .from(schema.estimates)
        .where(eq(schema.estimates.id, data.id));
      const previous = before ? fromRow(before) : undefined;

      // Статус согласования обычная запись не меняет: он берётся из базы, а
      // не из того, что прислали. Переходы идут через `performReview` — с
      // экрана и из Телеграма одним путём. Единственное исключение внутри
      // стража: правка содержания согласованной сметы согласование снимает.
      const actor = { email: context.auth.email, role: context.auth.role };
      const { estimate: guarded, dropped } = guardReview(
        incoming,
        previous,
        actor,
        new Date().toISOString(),
      );
      if (dropped) console.warn(`[review] смета ${data.id}: правка после согласования сняла его`);
      // Снимок ставки — при каждой записи, построчно. Флаг `ratecardVersion`
      // говорит только о том, что смету однажды заморозили целиком: строка,
      // добавленная кнопкой под роль, которой тогда не было в прайсе, снимка
      // не получала — и начинала считаться по прайсу задним числом, стоило
      // админу завести эту роль. `freezeLine` уже заполненное не трогает.
      const estimate: BrainingEstimate = freezeEstimateRates(
        { ...guarded, savedAt: new Date().toISOString() },
        await currentRatecard(),
      );
      const cols = indexColumns(estimate);

      // Пишем, только если в базе лежит та же версия, с которой правили.
      // Смета без отметки — записана до этой проверки, её перезаписываем.
      const base = incoming.savedAt;
      const [updated] = await db
        .update(schema.estimates)
        .set({ ...cols, data: estimate, updatedAt: new Date().toISOString() })
        .where(
          and(
            eq(schema.estimates.id, data.id),
            base
              ? sql`coalesce(${schema.estimates.data}->>'savedAt', ${base}) = ${base}`
              : sql`true`,
          ),
        )
        .returning({ id: schema.estimates.id, data: schema.estimates.data });
      if (updated) {
        const saved = fromRow(updated);
        void auditEstimate(previous, saved, context.auth.userId);
        return sanitizeForSeroval("saveEstimate", saved);
      }

      // Ноль строк — либо калькулятор чужой (RLS), либо его успели записать
      // из другой вкладки. Молча «сохранить» в никуда хуже, чем сказать правду.
      const [current] = await db
        .select({ id: schema.estimates.id, data: schema.estimates.data })
        .from(schema.estimates)
        .where(eq(schema.estimates.id, data.id));
      if (!current) throw new Response("Not found", { status: 404 });
      return sanitizeForSeroval("saveEstimate", {
        conflict: true as const,
        current: fromRow(current),
      });
    }

    // Новая смета рождается черновиком: статус появляется только переходами.
    const born = guardReview(
      incoming,
      undefined,
      { email: context.auth.email, role: context.auth.role },
      new Date().toISOString(),
    ).estimate;
    const fresh: BrainingEstimate = freezeEstimateRates(
      {
        ...born,
        savedAt: new Date().toISOString(),
      },
      await currentRatecard(),
    );
    const [created] = await db
      .insert(schema.estimates)
      .values({ userId: getCurrentUserId(), ...indexColumns(fresh), data: fresh })
      .returning({ id: schema.estimates.id, data: schema.estimates.data });
    const saved = fromRow(created);
    void auditEstimate(undefined, saved, context.auth.userId);
    return sanitizeForSeroval("saveEstimate", saved);
  });

/** Архив и корзина — отметки времени в самой смете плюс те же поля-индексы. */
async function stamp(id: string, field: "archivedAt" | "trashedAt", on: boolean) {
  const db = getDb();
  const [row] = await db
    .select({ id: schema.estimates.id, data: schema.estimates.data })
    .from(schema.estimates)
    .where(eq(schema.estimates.id, id));
  if (!row) throw new Response("Not found", { status: 404 });

  const next: BrainingEstimate = {
    ...fromRow(row),
    [field]: on ? new Date().toISOString() : undefined,
  };
  await db
    .update(schema.estimates)
    .set({ ...indexColumns(next), data: next, updatedAt: new Date().toISOString() })
    .where(eq(schema.estimates.id, id));
}

const _archiveEstimate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => IdFlag.parse(d))
  .handler(async ({ data }) => stamp(data.id, "archivedAt", data.on));

const _trashEstimate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => IdFlag.parse(d))
  .handler(async ({ data }) => stamp(data.id, "trashedAt", data.on));

const _purgeEstimate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: unknown) => IdOnly.parse(d))
  .handler(async ({ data }): Promise<void> => {
    await getDb().delete(schema.estimates).where(eq(schema.estimates.id, data.id));
  });

export const loadEstimates = _loadEstimates;
export const saveEstimate = _saveEstimate;
export const archiveEstimate = _archiveEstimate;
export const trashEstimate = _trashEstimate;
export const purgeEstimate = _purgeEstimate;
